import { Directive, ElementRef, HostListener, Input, OnDestroy, Renderer2 } from '@angular/core';

@Directive({
  selector: '[eeTableStickyHeader]',
  standalone: false
})
export class TableStickyHeaderDirective implements OnDestroy {
  private header: HTMLElement;
  private table: HTMLElement;
  private clone: HTMLElement;
  private isCloneCreated = false;

  @Input() stickyPaddingTop = 0;
  @Input() disableStickiness = false;

  constructor(private el: ElementRef, private renderer: Renderer2) {}

  private createClone() {
    // Create a clone of the header
    this.clone = this.header.cloneNode(true) as HTMLElement;
    this.renderer.setStyle(this.clone, 'position', 'fixed');
    this.renderer.setStyle(this.clone, 'top', '0');
    this.renderer.setStyle(this.clone, 'zIndex', '25');
    this.renderer.setStyle(this.clone, 'background', 'white'); // Assuming white background, adjust as needed
    this.renderer.setStyle(this.clone, 'width', `${this.header.offsetWidth}px`);
    this.renderer.setStyle(this.clone, 'display', 'none'); // Hide initially

    if (this.stickyPaddingTop) {
      this.renderer.setStyle(this.clone, 'paddingTop', `${this.stickyPaddingTop}px`);
    }

    // Add to a table wrapper with the same class as the table
    const tableWrapper = document.createElement('table');
    tableWrapper.classList.add(this.table.classList[0]);
    this.renderer.appendChild(tableWrapper, this.clone);

    // Center the table wrapper
    this.renderer.setStyle(tableWrapper, 'margin', '0 auto');

    // Append the clone to the document body
    this.renderer.appendChild(document.body, tableWrapper);

    // Hide the original header when it's out of view
    this.renderer.setStyle(this.header, 'visibility', 'hidden');
  }

  @HostListener('window:scroll')
  onWindowScroll() {
    if (this.disableStickiness) return;

    if (!this.isCloneCreated) {
      this.table = this.el.nativeElement;
      this.header = this.table.querySelector('thead');
      this.createClone();
      this.isCloneCreated = true;
    }

    this.checkStickiness();
  }

  @HostListener('window:resize')
  onWindowResize() {
    if (!this.disableStickiness && this.clone) {
      this.renderer.setStyle(this.clone, 'width', `${this.header.offsetWidth}px`);
      this.updateClonePosition();
    }
  }

  private checkStickiness() {
    if (!this.header || !this.table || !this.clone) return;

    const tableRect = this.table.getBoundingClientRect();
    const headerRect = this.header.getBoundingClientRect();

    if (tableRect.top <= 0 && tableRect.bottom > headerRect.height) {
      // Show the clone when the header is partially out of view
      this.renderer.setStyle(this.clone, 'display', 'block');
      this.renderer.setStyle(this.header, 'visibility', 'hidden');

      // Adjust clone width to match table width when scrolling
      this.renderer.setStyle(this.clone, 'width', `${tableRect.width}px`);
      this.updateClonePosition();
    } else {
      // Hide the clone and show the original header when it's back in view
      this.renderer.setStyle(this.clone, 'display', 'none');
      this.renderer.setStyle(this.header, 'visibility', 'visible');
    }
  }

  private updateClonePosition() {
    const tableRect = this.table.getBoundingClientRect();
    this.renderer.setStyle(this.clone, 'left', `${tableRect.left}px`);
    this.renderer.setStyle(this.clone, 'right', `${window.innerWidth - tableRect.right}px`);
  }

  ngOnDestroy() {
    if (this.clone && this.clone.parentNode) {
      this.renderer.removeChild(document.body, this.clone);
    }
  }
}
